// src/components/client/StylistPortfolio.tsx
import React, { useMemo, useState, useEffect } from 'react';
import portfolioService from '../../services/portfolioService';
import type { PortfolioImage } from '../../types/portfolio';
import MediaViewer, { MediaViewerItem } from '../common/MediaViewer';
import {
  resolvePublicMediaUrl,
  isProbablyVideoUrl,
  normalizeMediaUrlString,
  resolveWebImageSrc,
} from '../../utils/resolveMediaUrl';

interface StylistPortfolioProps {
  stylistId: string;
}

const isPortfolioVideo = (item: PortfolioImage): boolean => {
  const rawStr = normalizeMediaUrlString(item.url ?? item.imageUrl);
  const mediaUrl = resolvePublicMediaUrl(rawStr);
  const looksLikeCloudinaryImage = /\/image\/upload\//i.test(mediaUrl);
  return (
    !looksLikeCloudinaryImage &&
    (item.mediaType === 'video' || isProbablyVideoUrl(mediaUrl))
  );
};

const StylistPortfolio: React.FC<StylistPortfolioProps> = ({ stylistId }) => {
  const [portfolioItems, setPortfolioItems] = useState<PortfolioImage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  useEffect(() => {
    fetchPortfolio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stylistId]); // fetchPortfolio is stable
  
  const fetchPortfolio = async () => {
    try {
      setLoading(true);
      // Get portfolio data using the correct method name
      const response = await portfolioService.getPortfolio(stylistId);
      
      // Extract images from all portfolio categories
      const allImages = (response.portfolio?.flatMap((category) => category.images) || []).filter(
        (item) => Boolean(normalizeMediaUrlString(item.url ?? item.imageUrl))
      );
      setPortfolioItems(allImages);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching portfolio:', err);
      setError('Failed to load portfolio');
      setLoading(false);
    }
  };

  const imageItems = useMemo(
    () => portfolioItems.filter((item) => !isPortfolioVideo(item)),
    [portfolioItems],
  );

  const viewerItems: MediaViewerItem[] = useMemo(
    () =>
      imageItems.map((item) => ({
        type: 'image' as const,
        src: resolveWebImageSrc(item.url ?? item.imageUrl),
      })),
    [imageItems],
  );

  const openViewer = (item: PortfolioImage) => {
    const index = imageItems.findIndex((img) => img._id === item._id);
    if (index >= 0) {
      setSelectedIndex(index);
      setViewerVisible(true);
    }
  };
  
  if (loading) {
    return (
      <div className="portfolio__loading">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="message message--error">
        {error}
      </div>
    );
  }
  
  if (portfolioItems.length === 0) {
    return (
      <div className="message message--info">
        No portfolio items available yet.
      </div>
    );
  }
  
  return (
    <div className="portfolio">
      <h3 className="portfolio__title">
        Portfolio
      </h3>
      
      <div className="portfolio-grid">
        {portfolioItems.map((item) => {
          const rawStr = normalizeMediaUrlString(item.url ?? item.imageUrl);
          const mediaUrl = resolvePublicMediaUrl(rawStr);
          const isVideo = isPortfolioVideo(item);
          const imageSrc = resolveWebImageSrc(item.url ?? item.imageUrl);
          return (
          <div key={item._id} className="portfolio-item">
            <div
              className={`portfolio-item__image-container${isVideo ? '' : ' portfolio-item__image-container--clickable'}`}
              onClick={isVideo ? undefined : () => openViewer(item)}
              onKeyDown={
                isVideo
                  ? undefined
                  : (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openViewer(item);
                      }
                    }
              }
              role={isVideo ? undefined : 'button'}
              tabIndex={isVideo ? undefined : 0}
              aria-label={isVideo ? undefined : `View portfolio image${item.caption ? `: ${item.caption}` : ''}`}
            >
              {isVideo ? (
                <video
                  src={mediaUrl}
                  className="portfolio-item__image"
                  aria-label={item.caption || 'Portfolio video'}
                  controls
                  playsInline
                  muted
                  preload="metadata"
                />
              ) : (
                <img
                  src={imageSrc}
                  alt={item.caption || 'Portfolio image'}
                  className="portfolio-item__image"
                  onError={(e) => {
                    const el = e.currentTarget;
                    if (el.dataset.imgFallback === '1') return;
                    el.dataset.imgFallback = '1';
                    if (mediaUrl && imageSrc !== mediaUrl) {
                      el.src = mediaUrl;
                    }
                  }}
                />
              )}
            </div>
            {(item.caption || (item.tags && item.tags.length > 0)) && (
              <div className="portfolio-item__content">
                {item.caption && (
                  <p className="portfolio-item__caption">
                    {item.caption}
                  </p>
                )}
                {item.tags && item.tags.length > 0 && (
                  <div className="portfolio-item__tags">
                    {item.tags.map((tag: string, i: number) => (
                      <span key={i} className="portfolio-item__tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          );
        })}
      </div>

      <MediaViewer
        items={viewerItems}
        open={viewerVisible}
        index={selectedIndex}
        onClose={() => setViewerVisible(false)}
      />
    </div>
  );
};

export default StylistPortfolio;